import { apiClient } from '@/lib/api/client';
import {
  bannerAdminListSchema,
  portalBannersResponseSchema,
  type BannerAdmin,
  type BannerAdminList,
  type BannerFormulario,
  type PortalBanner,
} from '../types/announcement.types';

export const announcementService = {
  /**
   * Banners activos y vigentes para el usuario actual.
   *
   * Quién ve qué lo decide el servidor (segmentación por tenant, compañía y
   * rol): acá no llega ningún banner que no toque, así que no hay nada que
   * filtrar del lado del cliente.
   *
   * Un banner que no valide contra el esquema se descarta y los demás se
   * muestran igual — el JSON de origen se edita a mano y no vale tirar toda la
   * franja porque un registro tenga un campo de menos.
   */
  async getBanners(): Promise<PortalBanner[]> {
    const response = await apiClient.get('/banners');
    const parsed = portalBannersResponseSchema.safeParse(response.data);

    if (parsed.success) return parsed.data.banners;

    const banners = (response.data as { banners?: unknown[] })?.banners;
    if (!Array.isArray(banners)) return [];

    return banners.flatMap((b) => {
      const item = portalBannersResponseSchema.shape.banners.element.safeParse(b);
      return item.success ? [item.data] : [];
    });
  },

  // -------------------------------------------------------------------------
  // Administración — requiere SUPER ADMIN. El backend lo exige igual, así que
  // un 403 aquí es la red de seguridad, no la primera línea.
  // -------------------------------------------------------------------------

  /** El archivo completo: activos, apagados y vencidos. */
  async listarAdmin(): Promise<BannerAdminList> {
    const response = await apiClient.get('/banners/admin');
    return bannerAdminListSchema.parse(response.data);
  },

  async crear(banner: BannerFormulario): Promise<BannerAdmin> {
    const response = await apiClient.post('/banners/admin', banner);
    return response.data.banner as BannerAdmin;
  },

  async actualizar(id: string, cambios: Partial<BannerFormulario>): Promise<BannerAdmin> {
    const response = await apiClient.put(`/banners/admin/${id}`, cambios);
    return response.data.banner as BannerAdmin;
  },

  async borrar(id: string): Promise<void> {
    await apiClient.delete(`/banners/admin/${id}`);
  },
};
